import React, { useCallback, useEffect, useState } from 'react';
import '../Reports/Reports.scss';
import ViewReports from '../../components/ViewReports/ViewReports';
import ReportsPdfData from '../../components/ReportsPdfData/ReportsPdfData';
import reportpdfLogo from '../../assets/images/reportpdf-logo.png';
import { DEVICE, MONTHS, REPORT } from '../../utils/const';
import useApi from '../../hook/useAPI';
import { useContextValue } from '../../context/LoaderContext';
import {
  buildDeviceNameMap,
  formatDeviceNamesList,
  resolveDeviceNames,
} from '../../utils/reportDevices';
import { APIProvider } from '@vis.gl/react-google-maps';
import { convert } from '../../utils/moment';
import { GOOGLE_KEY } from '../../constants/Constant';

const STATUS_COLUMNS = [
  { header: 'Device', key: 'device' },
  { header: 'Status', key: 'status' },
  { header: 'Location', key: 'location' },
  { header: 'Command', key: 'command' },
  { header: 'Battery', key: 'battery' },
  { header: 'Date / Time', key: 'date' },
];

const TRIP_COLUMNS = [
  { header: 'Device Name', key: 'device' },
  { header: 'Start Location', key: 'startlocation' },
  { header: 'End Location', key: 'endlocation' },
  { header: 'Duration', key: 'duration' },
  { header: 'Distance Traveled', key: 'distance' },
  { header: 'Date / Time', key: 'date' },
];

const ZONE_COLUMNS = [
  { header: 'Device Name', key: 'deviceName' },
  { header: 'Zone Name', key: 'name' },
  { header: 'Zone Address', key: 'location' },
  { header: 'Duration', key: 'duration' },
  { header: 'Distance Radius', key: 'distance' },
  { header: 'Date / Time', key: 'date' },
];

function Reports() {
  const [reportTitle, setReportTitle] = useState('');
  const [deviceNamesLabel, setDeviceNamesLabel] = useState('');
  const [timePeriod, setTimePeriod] = useState('');
  const [deviceNameById, setDeviceNameById] = useState({});
  const [statusRowData, setStatusRowData] = useState([]);
  const [tripsData, setTripsData] = useState([]);
  const [zoneData, setZoneData] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const { getData } = useApi();
  const { reportPdfRef } = useContextValue();

  const loadDeviceNameMap = useCallback(async () => {
    try {
      const response = await getData(DEVICE.GET_CURRENT_DEVICE_STATUS, null, false);
      if (response?.status === 200 && Array.isArray(response.data)) {
        setDeviceNameById(buildDeviceNameMap(response.data));
      }
    } catch (error) {
      console.error('Error fetching devices for report labels:', error);
    }
  }, [getData]);

  useEffect(() => {
    loadDeviceNameMap();
  }, [loadDeviceNameMap]);

  const countDaysBetweenDates = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const timeDifference = end - start;
    return Math.abs(timeDifference / (1000 * 3600 * 24));
  };

  const formatDate = async (date) => {
    if (!date) {
      return '-';
    }
    const parsed = new Date(date);
    const day = parsed.getUTCDate();
    const month = MONTHS[parsed.getUTCMonth()];
    const year = parsed.getUTCFullYear();
    const hours = String(parsed.getUTCHours()).padStart(2, '0');
    const minutes = String(parsed.getUTCMinutes()).padStart(2, '0');
    const seconds = String(parsed.getUTCSeconds()).padStart(2, '0');
    return `${day} ${month} ${year} ${hours}:${minutes}:${seconds}`;
  };

  const getDurationLabel = (startTime, endTime) => {
    const durationInMilliseconds = endTime - startTime;
    const totalSeconds = Math.floor(durationInMilliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    let formattedDuration = '';
    if (hours > 0) {
      formattedDuration += `${hours} hr `;
    }
    if (minutes > 0 || hours > 0) {
      formattedDuration += `${minutes} min `;
    }
    if (seconds > 0 || (!hours && !minutes)) {
      formattedDuration += `${seconds} sec`;
    }
    return formattedDuration.trim() || '-';
  };

  const getAddress = (latitude, longitude) => new Promise((resolve) => {
    if (latitude && longitude && window?.google) {
      const geocoder = new window.google.maps.Geocoder();
      const location = { lat: parseFloat(latitude), lng: parseFloat(longitude) };
      geocoder.geocode({ location }, (results, status) => {
        if (status === 'OK' && results[0]) {
          resolve(results[0].formatted_address);
          return;
        }
        resolve('-');
      });
      return;
    }
    resolve('-');
  });

  const clearReportDetail = () => {
    setStatusRowData([]);
    setTripsData([]);
    setZoneData([]);
    setReportTitle('');
    setDeviceNamesLabel('');
    setTimePeriod('');
  };

  const loadReportDetails = async (id) => {
    if (!id) {
      clearReportDetail();
      return;
    }

    setIsLoading(true);
    setLoadError('');

    try {
      localStorage.setItem('current_report_export_id', id);
      const response = await getData(`${REPORT.GET_REPORT}/${id}`, null, false);

      if (!(response?.status === 200 || response?.status === true)) {
        clearReportDetail();
        setLoadError('Unable to load this report.');
        return;
      }

      const report = response?.data?.report;
      const reportsData = response?.data?.reportsData || [];
      const allDeviceStatuses = reportsData.flatMap((entry) => entry.deviceStatus || []);
      const allDeviceTrips = reportsData.flatMap((entry) => entry.trips || []);
      const allDeviceZoneActivities = reportsData.flatMap((entry) => entry.zoneActivities || []);

      const formattedStatus = await Promise.all(allDeviceStatuses.map(async (statusObj) => ({
        device: statusObj?.deviceName || '-',
        status: statusObj?.eventType || '-',
        location: await getAddress(statusObj?.latitude, statusObj?.longitude),
        command: statusObj?.command || '-',
        battery: `${statusObj?.batteryLevel || 0}%`,
        date: await formatDate(statusObj?.dateTime),
      })));
      setStatusRowData(formattedStatus);

      setReportTitle(report?.name || '');
      const resolvedNames = resolveDeviceNames(
        report?.deviceIds,
        reportsData,
        deviceNameById,
      );
      setDeviceNamesLabel(formatDeviceNamesList(resolvedNames));
      if (report?.startDate && report?.endDate) {
        const days = countDaysBetweenDates(report.startDate, report.endDate) || 0;
        setTimePeriod(`${days + 1} ${days > 0 ? 'Days' : 'Day'}`);
      } else {
        setTimePeriod('');
      }

      const formattedTrips = await Promise.all(allDeviceTrips.map(async (tripsObj) => {
        const [trip] = tripsObj?.trips || [];
        if (!trip) {
          return null;
        }
        const endTime = new Date(trip.end_time).getTime();
        const startTime = new Date(trip.final_adjust_ime).getTime();
        return {
          device: trip?.deviceName || tripsObj?.deviceName || '-',
          startlocation: trip.start_address || '-',
          endlocation: trip.end_address || '-',
          duration: getDurationLabel(startTime, endTime),
          distance: trip.distance ? `${trip.distance} Miles` : '-',
          date: convert(new Date(trip.dateTime), 'DD MMM YYYY HH:mm:ss'),
        };
      }));
      setTripsData(formattedTrips.filter(Boolean));

      const formattedZones = allDeviceZoneActivities.map((zoneObj) => {
        const zoneArr = zoneObj.zoneActivities || [];
        const first = zoneArr[0];
        if (!first) {
          return null;
        }
        const startTime = new Date(first.dateTime);
        const endTime = new Date(zoneArr[zoneArr.length - 1]?.dateTime);
        return {
          deviceName: first.deviceName || '-',
          name: first.zoneName || '-',
          location: first.zone_address || '-',
          duration: getDurationLabel(startTime, endTime),
          distance: first.zoneRadius ? `${first.zoneRadius} miles` : '-',
          date: convert(endTime, 'DD MMM YYYY HH:mm:ss'),
        };
      });
      setZoneData(formattedZones.filter(Boolean));
    } catch (error) {
      console.error('Error fetching report details:', error);
      clearReportDetail();
      setLoadError('Unable to load this report.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadReportDetails(selectedReport);
  }, [selectedReport, deviceNameById]);

  const hasDetailSections = statusRowData.length > 0
    || tripsData.length > 0
    || zoneData.length > 0;

  return (
    <APIProvider apiKey={GOOGLE_KEY}>
      <div className="dashboard reports-page">
        <div className="row g-4">
          <div className="col-lg-5 col-xl-4">
            <ViewReports
              selectedReportId={selectedReport}
              onSelectReport={setSelectedReport}
            />
          </div>

          <div className="col-lg-7 col-xl-8">
            <div className="card reports-detail-card h-100">
              <div className="card-body">
                {isLoading && (
                  <div className="reports-detail-state">Loading report data…</div>
                )}

                {!isLoading && loadError && (
                  <div className="reports-detail-state reports-detail-state--error">{loadError}</div>
                )}

                {!isLoading && !loadError && !selectedReport && (
                  <div className="reports-detail-state">
                    <h4>Select a report</h4>
                    <p>Choose a saved report from the list or create a new one to preview export data.</p>
                  </div>
                )}

                {!isLoading && !loadError && selectedReport && (
                  <>
                    <div ref={reportPdfRef} className="reports-export-capture">
                      <div className="reports-pdfdata-head">
                        <div className="reports-pdfdata-main">
                          <h2 className="reports-report-name">{reportTitle || '—'}</h2>
                          <div className="reports-meta-grid">
                            <div className="reports-meta-item">
                              <span className="reports-meta-label">Devices</span>
                              <span className="reports-meta-value">{deviceNamesLabel}</span>
                            </div>
                            <div className="reports-meta-item">
                              <span className="reports-meta-label">Time period</span>
                              <span className="reports-meta-value">{timePeriod || '—'}</span>
                            </div>
                          </div>
                        </div>
                        <div className="reports-pdfdata-logo">
                          <img src={reportpdfLogo} alt="LocShark" />
                        </div>
                      </div>

                    {statusRowData.length > 0 && (
                      <ReportsPdfData
                        title="Current Status"
                        columnsOptions={STATUS_COLUMNS}
                        rowData={statusRowData}
                      />
                    )}

                    {tripsData.length > 0 && (
                      <ReportsPdfData
                        title="Recent Trips"
                        columnsOptions={TRIP_COLUMNS}
                        rowData={tripsData}
                      />
                    )}

                    {zoneData.length > 0 && (
                      <ReportsPdfData
                        title="Zone Activity"
                        columnsOptions={ZONE_COLUMNS}
                        rowData={zoneData}
                      />
                    )}

                    {!hasDetailSections && (
                      <p className="reports-detail-empty-note">
                        No activity rows for this period. You can still download a PDF of the report summary.
                      </p>
                    )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </APIProvider>
  );
}

export default Reports;
